import TimeSlotPicker from '@/components/TimeSlotPicker';
import { Button } from '@/components/ui/button';
import { Calendar as DateCalendar } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import type { DaySlots } from '@/data/mock';
import type { BookingSelectedTime } from '@/types/booking-flow';
import { Calendar, Clock3 } from 'lucide-react';
import { useState } from 'react';

interface BookingDateTimeStepProps {
  allowWaitingList?: boolean;
  availabilityDays: DaySlots[];
  hasMoreAvailability: boolean;
  isLoadingAvailability: boolean;
  onAvailabilityDateSelect: (date?: Date) => void;
  onJoinWaitingList?: (preferredTime: string | null) => void;
  onLoadMoreAvailability: () => void;
  onSelectTime: (date: string, time: string, label: string) => void;
  onWaitingListPreferredTimeChange?: (value: string) => void;
  selectedAvailabilityDate?: Date;
  selectedTime: BookingSelectedTime | null;
  today: Date;
  waitingListPreferredTime?: string;
}

function formatAvailabilityDateLabel(date?: Date) {
  if (!date) {
    return 'A partir de hoje';
  }

  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'long',
  });
}

const BookingDateTimeStep = ({
  allowWaitingList = false,
  availabilityDays,
  hasMoreAvailability,
  isLoadingAvailability,
  onAvailabilityDateSelect,
  onJoinWaitingList,
  onLoadMoreAvailability,
  onSelectTime,
  onWaitingListPreferredTimeChange,
  selectedAvailabilityDate,
  selectedTime,
  today,
  waitingListPreferredTime = '',
}: BookingDateTimeStepProps) => {
  const waitingListTimeValid = /^([01]\d|2[0-3]):([0-5]\d)$/.test(waitingListPreferredTime);
  const [waitingListMode, setWaitingListMode] = useState<'any' | 'specific'>(
    waitingListPreferredTime ? 'specific' : 'any',
  );
  const activeWaitingListMode = waitingListPreferredTime ? 'specific' : waitingListMode;

  return (
    <div className="animate-fade-in-up">
      <p className="text-muted-foreground mb-5 text-sm">Escolha o melhor horário para você</p>
      <div className="border-border bg-card mb-4 flex items-center justify-between gap-3 rounded-xl border p-4">
        <div>
          <p className="text-card-foreground text-sm font-medium">Explorar outras datas</p>
          <p className="text-muted-foreground text-xs">Abra o calendário para buscar dias mais à frente.</p>
        </div>
        <Popover>
          <PopoverTrigger asChild>
            <Button type="button" variant="outline" className="rounded-xl">
              <Calendar className="h-4 w-4" />
              <span>{formatAvailabilityDateLabel(selectedAvailabilityDate)}</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-auto p-0">
            <DateCalendar
              mode="single"
              selected={selectedAvailabilityDate}
              today={today}
              onSelect={onAvailabilityDateSelect}
              disabled={(date) => date < today}
            />
          </PopoverContent>
        </Popover>
      </div>
      {isLoadingAvailability && availabilityDays.length === 0 ? (
        <p className="bg-muted text-muted-foreground rounded-xl py-4 text-center text-sm">
          Buscando horários disponíveis...
        </p>
      ) : availabilityDays.length === 0 ? (
        allowWaitingList && selectedAvailabilityDate ? (
          <div className="border-border bg-card space-y-4 rounded-xl border p-5">
            <div className="space-y-2">
              <p className="text-card-foreground text-base font-semibold">Sem horário para esta data</p>
              <p className="text-muted-foreground text-sm">
                Entre na fila de espera para {formatAvailabilityDateLabel(selectedAvailabilityDate)}.
              </p>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <button
                type="button"
                onClick={() => {
                  setWaitingListMode('any');
                  onWaitingListPreferredTimeChange?.('');
                }}
                className={`rounded-xl border p-4 text-left transition-colors ${
                  activeWaitingListMode === 'any'
                    ? 'border-primary bg-primary/5'
                    : 'border-border bg-background hover:border-primary/30'
                }`}
              >
                <p className="text-card-foreground font-medium">Qualquer horário</p>
                <p className="text-muted-foreground mt-1 text-sm">Avisa se qualquer vaga desse dia abrir.</p>
              </button>
              <button
                type="button"
                onClick={() => setWaitingListMode('specific')}
                className={`rounded-xl border p-4 text-left transition-colors ${
                  activeWaitingListMode === 'specific'
                    ? 'border-primary bg-primary/5'
                    : 'border-border bg-background hover:border-primary/30'
                }`}
              >
                <p className="text-card-foreground font-medium">Escolher horário</p>
                <p className="text-muted-foreground mt-1 text-sm">Avisa só se abrir perto do horário desejado.</p>
              </button>
            </div>

            {activeWaitingListMode === 'specific' && (
              <div className="space-y-2">
                <label htmlFor="waiting-list-time" className="text-card-foreground text-sm font-medium">
                  Horário desejado
                </label>
                <div className="relative">
                  <Clock3 className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                  <Input
                    id="waiting-list-time"
                    type="time"
                    value={waitingListPreferredTime}
                    onChange={(event) => onWaitingListPreferredTimeChange?.(event.target.value)}
                    className="h-12 rounded-xl pl-10"
                  />
                </div>
                <p className="text-muted-foreground text-xs">Você pode ajustar esse horário depois, se quiser.</p>
              </div>
            )}

            <Button
              type="button"
              className="h-12 w-full rounded-xl"
              disabled={activeWaitingListMode === 'specific' && !waitingListTimeValid}
              onClick={() =>
                onJoinWaitingList?.(activeWaitingListMode === 'specific' ? waitingListPreferredTime : null)
              }
            >
              {activeWaitingListMode === 'specific' ? 'Entrar na fila desse horário' : 'Entrar na fila desse dia'}
            </Button>
          </div>
        ) : (
          <p className="bg-muted text-muted-foreground rounded-xl py-4 text-center text-sm">
            Nenhum horário disponível para a seleção atual.
          </p>
        )
      ) : (
        <TimeSlotPicker
          days={availabilityDays}
          selectedTime={selectedTime ? { date: selectedTime.date, time: selectedTime.time } : null}
          onSelectTime={onSelectTime}
          hasMore={hasMoreAvailability}
          onLoadMore={onLoadMoreAvailability}
        />
      )}
    </div>
  );
};

export default BookingDateTimeStep;
